import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { api } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { useApi } from "../useApi";

interface Entry {
  key: string;
  value: string;
  source: string;
  editable_in: string;
  label?: string;
  help?: string;
  allowed?: string;
}

interface Connection {
  name: string;
  enabled: boolean;
  detail: string;
  ready: boolean;
  problem: string;
}

interface View {
  environment: string;
  llm_profile: string;
  rule_version: number | null;
  thresholds: Entry[];
  retention: Entry[];
  guard: Entry[];
  connections: Connection[];
  warnings: string[];
}

function Table({ rows }: { rows: Entry[] }): JSX.Element {
  return (
    <table className="rows">
      <thead>
        <tr>
          <th>Beállítás</th>
          <th>Érték</th>
          <th>Hol módosítható</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td className="mono">{row.key}</td>
            <td>
              <strong>{row.value}</strong>
              {row.source === "alapertelmezes" && (
                <span className="muted"> (alapértelmezés)</span>
              )}
            </td>
            <td className="muted">{row.editable_in}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A küszöbök — a rendelő a Drive-mappa „Beállítások” táblázatában állítja.
 *
 * A táblázatba a BAL oszlop szövegét kell írni (nem technikai kulcsot), ezért
 * a nézet pontosan azt mutatja: innen egy az egyben átmásolható.
 */
function Thresholds({ rows }: { rows: Entry[] }): JSX.Element {
  return (
    <>
      <table className="rows">
        <thead>
          <tr>
            <th>Beállítás</th>
            <th>Érték</th>
            <th>Megengedett</th>
            <th>Mit jelent</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <strong>{row.label || row.key}</strong>
              </td>
              <td>
                <strong>{row.value}</strong>{" "}
                {row.source === "sheet" ? (
                  <Pill tone="ok">a táblázatból</Pill>
                ) : (
                  <Pill tone="muted">beépített</Pill>
                )}
              </td>
              <td className="muted">{row.allowed}</td>
              <td className="muted">{row.help}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <TemplateDownload />
    </>
  );
}

/**
 * A kitölthető beállítás-táblázat letöltése.
 *
 * A rendelő visszajelzése (2026-10-16): a leírás alapján üres lapról kellett
 * volna felépítenie a táblázatot, a pontos sorszövegekkel — nem készült el.
 * A sablont csak ki kell tölteni és a Drive-mappába tenni.
 */
function TemplateDownload(): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const letolt = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.download("/admin/v1/settings/template", "Kroki-beallitasok.xlsx");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notice notice--info">
      <p>
        <strong>Módosítás három lépésben:</strong> töltse le a kitölthető táblázatot, írja be
        az új értéket az <strong>Érték</strong> oszlopba annál a sornál, amelyiket módosítani
        szeretné (a többi maradhat üresen), és tegye a táblázatot a Drive-mappába, az árlista
        mellé. A következő árlista-szinkronnal életbe lép; a megengedett tartományon kívüli érték
        kimarad, és erről értesítés érkezik.
      </p>
      <Button onClick={() => void letolt()} disabled={busy}>
        {busy ? "Letöltés…" : "Kitölthető táblázat letöltése"}
      </Button>
      {error && <p className="notice notice--bad">{error}</p>}
    </div>
  );
}

export function SettingsView(): JSX.Element {
  const { data, error, loading } = useApi<View>("/admin/v1/settings");

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <Empty>Nincs megjeleníthető beállítás.</Empty>;

  return (
    <>
      {data.warnings.length > 0 && (
        <Card title="Befejezetlen beállítás">
          <ul className="plain">
            {data.warnings.map((w, i) => (
              <li key={i} className="notice notice--warn">
                {w}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Kapcsolatok" hint="Titkokat sosem jelenítünk meg — csak azt, be van-e állítva.">
        <table className="rows">
          <tbody>
            {data.connections.map((c) => (
              <tr key={c.name}>
                <td>{c.name}</td>
                <td>
                  {c.enabled ? (
                    c.ready ? (
                      <Pill tone="ok">bekapcsolva</Pill>
                    ) : (
                      <Pill tone="warn">hiányos</Pill>
                    )
                  ) : (
                    <Pill tone="muted">kikapcsolva</Pill>
                  )}
                </td>
                <td className="muted">
                  {c.detail}
                  {c.problem && <strong> — {c.problem}</strong>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        title="Küszöbök és szövegek"
        hint="A beutaló-felismerés és az árazás hangolása, valamint a chatablak szövegei."
      >
        <Thresholds rows={data.thresholds} />
      </Card>

      <Card title="Visszaélés-védelem" hint="A drága műveletek elé tett korlátok.">
        <Table rows={data.guard} />
      </Card>

      <Card title="Adatmegőrzés" hint="Mennyi ideig őrzünk meg mit (GDPR).">
        <Table rows={data.retention} />
      </Card>

      <Card title="Környezet">
        <dl className="facts">
          <div>
            <dt>Környezet</dt>
            <dd>{data.environment}</dd>
          </div>
          <div>
            <dt>LLM profil</dt>
            <dd>{data.llm_profile}</dd>
          </div>
          <div>
            <dt>Aktív szabályverzió</dt>
            <dd>{data.rule_version ? `#${data.rule_version}` : "nincs"}</dd>
          </div>
        </dl>
      </Card>
    </>
  );
}
